import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import UnitContentEditor, {
  type UnitObjectiveItem,
  cleanObjectives,
  areObjectiveQuestionsValid,
} from "../UnitContentEditor";
import "./index.scss";

export type {
  UnitObjectiveItem,
  UnitVideoQuestion,
  UnitVideoQuestionOption,
} from "../UnitContentEditor";

export interface UnitItem {
  id: string;
  name: string;
  /** Ünite içindeki dosyalar (her dosya bir objective olur) */
  objectives: UnitObjectiveItem[];
  /** Geriye dönük uyumluluk için (deprecated) */
  fileIDs: string[];
  durationHours?: number;
  durationMinutes?: number;
}

interface UnitAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (unit: UnitItem) => void;
  editingUnit?: UnitItem | null;
  supportedFormats?: string[];
  maxSize?: number;
}

const UnitAddModal: React.FC<UnitAddModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingUnit = null,
  supportedFormats = ["PDF", "DOC", "DOCX", "XLS", "XLSX"],
  maxSize = 500,
}) => {
  const { t } = useTranslation();
  const [unitName, setUnitName] = useState(editingUnit?.name || "");
  const [objectives, setObjectives] = useState<UnitObjectiveItem[]>(
    editingUnit?.objectives ??
      (editingUnit?.fileIDs ?? []).map((fileID, idx) => ({
        id: `obj-${idx}-${fileID}`,
        fileID,
        videoQuestions: [],
        allowSeek: true,
      })),
  );
  const [durationHours, setDurationHours] = useState(
    editingUnit?.durationHours != null ? String(editingUnit.durationHours) : "",
  );
  const [durationMinutes, setDurationMinutes] = useState(
    editingUnit?.durationMinutes != null
      ? String(editingUnit.durationMinutes)
      : "",
  );

  React.useEffect(() => {
    if (isOpen) {
      setUnitName(editingUnit?.name || "");
      setObjectives(
        editingUnit?.objectives ??
          (editingUnit?.fileIDs ?? []).map((fileID, idx) => ({
            id: `obj-${idx}-${fileID}`,
            fileID,
            videoQuestions: [],
            allowSeek: true,
          })),
      );
      setDurationHours(
        editingUnit?.durationHours != null
          ? String(editingUnit.durationHours)
          : "",
      );
      setDurationMinutes(
        editingUnit?.durationMinutes != null
          ? String(editingUnit.durationMinutes)
          : "",
      );
    }
  }, [isOpen, editingUnit]);

  const resetForm = () => {
    setUnitName("");
    setObjectives([]);
    setDurationHours("");
    setDurationMinutes("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isQuestionsValid = areObjectiveQuestionsValid(objectives);

  const handleSubmit = () => {
    if (!unitName.trim()) {
      return;
    }

    const hours = durationHours.trim() ? Number(durationHours) : undefined;
    const minutes = durationMinutes.trim()
      ? Number(durationMinutes)
      : undefined;
    if (
      (hours != null && (Number.isNaN(hours) || hours < 0)) ||
      (minutes != null &&
        (Number.isNaN(minutes) || minutes < 0 || minutes > 59))
    ) {
      return;
    }

    if (!isQuestionsValid) return;

    const cleaned = cleanObjectives(objectives);
    const unit: UnitItem = {
      id: editingUnit?.id || `unit-${Date.now()}`,
      name: unitName.trim(),
      objectives: cleaned.map((o) => ({
        ...o,
        videoQuestions: o.videoQuestions?.length ? o.videoQuestions : undefined,
      })),
      fileIDs: cleaned.map((o) => o.fileID),
      durationHours: hours,
      durationMinutes: minutes,
    };

    onSubmit(unit);
    handleClose();
  };

  const isFormValid =
    unitName.trim() !== "" && objectives.length > 0 && isQuestionsValid;

  const customFooter = (
    <div className="unit-add-modal-footer">
      <Button variant="text" color="secondary" onClick={handleClose}>
        {t("createEducation.unitModal.cancel")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!isFormValid}
      >
        {t("createEducation.unitModal.addUnit")}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("createEducation.unitModal.title")}
      size="lg"
      customFooter={customFooter}
    >
      <div className="unit-add-modal-content">
        {/* Unit Name Input */}
        <div className="form-field">
          <Input
            label={t("createEducation.unitModal.unitName")}
            placeholder={t("createEducation.unitModal.unitNamePlaceholder")}
            value={unitName}
            maxLength={200}
            onChange={(e) => setUnitName(e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label className="field-label">
            {t("createEducation.unitModal.duration")}
          </label>
          <div className="unit-duration-row">
            <Input
              type="number"
              min={0}
              placeholder={t("createEducation.unitModal.hours")}
              value={durationHours}
              onChange={(e) => setDurationHours(e.target.value)}
            />
            <Input
              type="number"
              min={0}
              max={59}
              placeholder={t("createEducation.unitModal.minutes")}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
            />
          </div>
        </div>

        <UnitContentEditor
          objectives={objectives}
          onObjectivesChange={setObjectives}
          supportedFormats={supportedFormats}
          maxSize={maxSize}
          seekControlReadOnly={!!editingUnit}
          showEducationVideoPool
        />
      </div>
    </Modal>
  );
};

export default UnitAddModal;
